import { useCallback, useRef, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { notifySupportApiNetworkError, notifySupportApiServerError } from '../utils/supportApiErrorNotifier';

// Timeout por defecto: 30 segundos
const FETCH_TIMEOUT = 30000;

export const useAvailability = () => {
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const abortControllerRef = useRef(null);
  const { language } = useLanguage();

  const checkAvailability = useCallback(async (startDate, endDate, signal = null) => {
    // Cancelar petición anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Crear nuevo AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Combinar señales si se proporciona una externa
    const finalSignal = signal || abortController.signal;

    try {
      setAvailabilityLoading(true);
      setAvailabilityError(null);
      
      // Validar fechas
      if (!startDate || !endDate) {
        throw new Error('Las fechas de inicio y fin son requeridas');
      }
      
      // Construir query parameters
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
        lang: language
      });
      
      const endpoint = `${API_ENDPOINTS.AVAILABILITY}?${params.toString()}`;
      console.log('Endpoint construido:', endpoint);
      const url = buildApiUrl(endpoint);
      console.log('URL completa de availability:', url);
      console.log('API_BASE_URL:', import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000');
      
      // Crear timeout con flag para distinguir timeout de cancelación manual
      let isTimeout = false;
      const timeoutId = setTimeout(() => {
        isTimeout = true;
        abortController.abort();
      }, FETCH_TIMEOUT);

      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: finalSignal
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        // Verificar si fue cancelado
        if (fetchError.name === 'AbortError') {
          // Si fue cancelado por timeout
          if (isTimeout) {
            throw new Error('La petición tardó demasiado. Por favor, intenta de nuevo.');
          }
          // Si fue cancelado manualmente, no lanzar error
          return null;
        }
        
        // Errores de red
        if (fetchError.message && (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError'))) {
          throw new Error(getTranslation('common.connection.error', language));
        }
        
        throw fetchError;
      }
      
      clearTimeout(timeoutId);
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        notifySupportApiServerError(response.status);
        const errorText = response.statusText || 'Error desconocido';
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('=== RESPUESTA DE AVAILABILITY DESDE FRONTEND ===');
      console.log('Tipo de datos:', typeof data);
      console.log('Es array:', Array.isArray(data));
      console.log('Longitud:', data?.length);
      console.log('Datos completos:', data);
      
      // Verificar si los datos tienen la estructura esperada
      if (data && data.length > 0) {
        console.log('Primer modelo:', data[0]);
        console.log('Campos del primer modelo:', Object.keys(data[0]));
        
        // Verificar si tiene los campos de temporada
        const firstModel = data[0];
        console.log('¿Tiene pricingType?', 'pricingType' in firstModel);
        console.log('¿Tiene season?', 'season' in firstModel);
        console.log('¿Tiene pricePerDay?', 'pricePerDay' in firstModel);
        console.log('¿Tiene totalPrice?', 'totalPrice' in firstModel);
        console.log('¿Tiene seasonBreakdown?', 'seasonBreakdown' in firstModel);
        
        // Buscar ALLTRACK 10 específicamente
        const alltrack = data.find(model => model.id === 4);
        if (alltrack) {
          console.log('=== ALLTRACK 10 ENCONTRADO ===');
          console.log('Datos completos:', alltrack);
          console.log('PricingType:', alltrack.pricingType);
          console.log('Season:', alltrack.season);
          console.log('PricePerDay:', alltrack.pricePerDay);
          console.log('TotalPrice:', alltrack.totalPrice);
          console.log('SeasonBreakdown:', alltrack.seasonBreakdown);
        } else {
          console.log('ALLTRACK 10 NO ENCONTRADO');
        }
      } else {
        console.log('No hay datos en la respuesta');
      }
      
      return data;
    } catch (err) {
      // No establecer error si fue cancelado manualmente (no por timeout)
      if (err.name === 'AbortError') {
        // Si fue cancelado manualmente, retornar null sin error
        // (el timeout ya lanzó su propio error antes)
        return null;
      }

      notifySupportApiNetworkError(err);
      
      console.error('Error checking availability:', err);
      const errorMessage = err.message || 'Error al cargar la disponibilidad. Por favor, intenta de nuevo.';
      setAvailabilityError(errorMessage);
      throw err;
    } finally {
      // Siempre resetear loading al finalizar (ya sea éxito, error o cancelación)
      setAvailabilityLoading(false);
      // Limpiar referencia si es la petición actual
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [language]);

  // Función para cancelar peticiones pendientes
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setAvailabilityLoading(false);
      setAvailabilityError(null);
    }
  }, []);

  return { 
    checkAvailability, 
    availabilityLoading, 
    availabilityError,
    cancelRequest
  };
}; 